'use client';

import { motion } from 'framer-motion';
import { useState } from 'react';
import { Plus, Trash2, Edit3, Sunrise, Sun, Sunset, Moon, AlertOctagon, Check, Calendar } from 'lucide-react';
import { generateTimeOptions, hasTimeOverlap, timeToMinutes, getConflictDetails } from '../utils';
import { Day, DayKey, Professional, TimeSlot } from '@/containers/CustomerJourneySetup/context/types';
import { Button } from '@/components/Button/ButtonV2';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/SelectV3';
import classNames from 'classnames';
import { Badge } from '@/components/Badge';

const getTimeInfo = (time: string) => {
  const minutes = timeToMinutes(time);

  if (minutes >= 300 && minutes < 420) {
    // desde las 5:00 am hasta las 7:00 am
    return {
      icon: <Sunrise className="h-4 w-4" />,
      period: 'Mañana temprano'
    };
  } else if (minutes >= 420 && minutes < 1080) {
    // desde las 7:00 am hasta las 6:00 pm
    return {
      icon: <Sun className="h-4 w-4" />,
      period: 'Día'
    };
  } else if (minutes >= 1080 && minutes < 1200) {
    // desde las 6:00 pm hasta las 8:00 pm
    return {
      icon: <Sunset className="h-4 w-4" />,
      period: 'Tarde'
    };
  } else {
    // desde las 8:00 pm hasta las 5:00 am
    return {
      icon: <Moon className="h-4 w-4" />,
      period: 'Noche'
    };
  }
};

interface WeeklyScheduleCardsProps {
  professional: Professional | undefined;
  days: Day[];
  onAddTimeSlot: (day: DayKey) => void;
  onUpdateTimeSlot: (day: DayKey, index: number, field: 'start' | 'end', value: string) => void;
  onDeleteTimeSlot: (day: DayKey, index: number) => void;
}

export const WeeklyScheduleCards = ({
  professional,
  days,
  onAddTimeSlot,
  onUpdateTimeSlot,
  onDeleteTimeSlot
}: WeeklyScheduleCardsProps) => {
  const timeOptions = generateTimeOptions();
  const [expandedDays, setExpandedDays] = useState<Set<DayKey>>(new Set());

  const toggleDayExpansion = (dayKey: DayKey) => {
    setExpandedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dayKey)) {
        newSet.delete(dayKey);
      } else {
        newSet.add(dayKey);
      }
      return newSet;
    });
  };

  // Auto-close expanded days when they have no time slots
  const handleDeleteTimeSlot = (dayKey: DayKey, index: number) => {
    // Call the parent's delete function first
    onDeleteTimeSlot(dayKey, index);

    // Check if this was the last slot for this day
    const daySlots = professional?.schedule?.[dayKey] || [];
    if (daySlots.length === 1) {
      // Will be 0 after deletion
      setExpandedDays((prev) => {
        const newSet = new Set(prev);
        newSet.delete(dayKey);
        return newSet;
      });
    }
  };

  // Auto-expand days when adding a new time slot
  const handleAddTimeSlot = (dayKey: DayKey) => {
    // Call the parent's add function first
    onAddTimeSlot(dayKey);

    // Auto-expand the day for editing the new slot
    setExpandedDays((prev) => {
      const newSet = new Set(prev);
      newSet.add(dayKey);
      return newSet;
    });
  };

  if (!professional) {
    return <p className="text-gray-500 text-lg">Cargando información del profesional...</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {days.map((day, dayIndex) => {
        const daySlots = professional?.schedule?.[day.key] || [];
        const hasOverlap = hasTimeOverlap(day.key, daySlots);
        const isExpanded = expandedDays.has(day.key);
        const totalSlots = daySlots.length;

        return (
          <div
            key={dayIndex}
            className={classNames(
              'group relative border rounded-2xl overflow-hidden transition-all duration-300',
              hasOverlap
                ? 'border-amber-200 bg-gradient-to-br from-amber-50/50 to-orange-50/30 '
                : isExpanded
                ? 'border-blue shadow-md shadow-blue/20'
                : 'border-gray-200 bg-white'
            )}
          >
            {/* Card content */}
            <div className="p-4">
              {hasOverlap && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <Badge
                    variant="outline"
                    className="text-amber-700 border-none bg-transparent font-medium !text-sm flex items-center gap-1 mb-2"
                  >
                    <AlertOctagon size={14} />
                    Horarios en conflicto
                  </Badge>
                  {(() => {
                    const conflicts = getConflictDetails(daySlots);
                    if (conflicts.length > 0) {
                      const conflict = conflicts[0]; // Show first conflict
                      return (
                        <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                          {conflict.slot1.start}-{conflict.slot1.end} se superpone con {conflict.slot2.start}-
                          {conflict.slot2.end}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              {/* Header del día */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-lg text-gray-900">{day.label}</h3>
                  </div>
                </div>

                {/* Status badges */}
                <div className="flex flex-col items-end gap-2">
                  {totalSlots > 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleDayExpansion(day.key)}
                      className={classNames(
                        'h-8 px-3 text-xs font-medium transition-all duration-200',
                        isExpanded
                          ? 'bg-blue/10 border-blue/50 text-blue'
                          : 'bg-white border-blue/50 text-blue'
                      )}
                    >
                      {isExpanded ? (
                        <div className="flex items-center gap-2">
                          <Check className="h-3 w-3" />
                          Guardar
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Edit3 className="h-3 w-3" />
                          Editar
                        </div>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddTimeSlot(day.key)}
                      className="border-blue/20 text-blue font-medium px-4 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Agregar horario
                      </div>
                    </Button>
                  )}
                </div>
              </div>

              {/* Contenido del día */}
              {daySlots.length > 0 && (
                <div className="space-y-4 mt-4">
                  {/* Lista de horarios */}
                  <div className="space-y-3">
                    {daySlots.map((slot: TimeSlot, index: number) => {
                      const startInfo = getTimeInfo(slot.start);
                      const endInfo = getTimeInfo(slot.end);
                      const isInvalid = timeToMinutes(slot.end) <= timeToMinutes(slot.start);
                      const duration = timeToMinutes(slot.end) - timeToMinutes(slot.start);
                      const durationHours = Math.floor(Math.max(0, duration) / 60);
                      const durationMinutes = Math.max(0, duration) % 60;

                      // Calcular límites de tiempo basándose en horarios previos y siguientes
                      const previousSlot = daySlots[index - 1];
                      const nextSlot = daySlots[index + 1];
                      const minTime = previousSlot ? previousSlot.end : undefined;
                      const maxTime = nextSlot ? nextSlot.start : undefined;

                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          className={classNames('relative transition-all duration-200')}
                        >
                          {isExpanded ? (
                            // Modo edición
                            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end pb-4 border-b border-gray-200">
                              {/* Time selectors en la misma fila */}
                              <div>
                                <label className="text-sm text-gray-600 mb-2 block font-medium">
                                  Hora inicio
                                </label>
                                <Select
                                  value={slot.start}
                                  onValueChange={(value) => onUpdateTimeSlot(day.key, index, 'start', value)}
                                >
                                  <SelectTrigger className="h-10 text-sm focus:border-blue transition-colors">
                                    <SelectValue placeholder="Selecciona hora" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-48">
                                    {timeOptions
                                      .filter((time) => {
                                        const timeMinutes = timeToMinutes(time);
                                        const minTimeMinutes = minTime ? timeToMinutes(minTime) : 0;
                                        const endTimeMinutes = timeToMinutes(slot.end);
                                        return timeMinutes >= minTimeMinutes && timeMinutes < endTimeMinutes;
                                      })
                                      .map((time) => (
                                        <SelectItem key={time} value={time} className="text-sm">
                                          {time}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-sm text-gray-600 mb-2 block font-medium">
                                  Hora fin
                                </label>
                                <Select
                                  value={slot.end}
                                  onValueChange={(value) => onUpdateTimeSlot(day.key, index, 'end', value)}
                                >
                                  <SelectTrigger className="h-10 text-sm focus:border-blue transition-colors">
                                    <SelectValue placeholder="Selecciona hora" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-48">
                                    {timeOptions
                                      .filter((time) => {
                                        const timeMinutes = timeToMinutes(time);
                                        const startTimeMinutes = timeToMinutes(slot.start);
                                        const maxTimeMinutes = maxTime ? timeToMinutes(maxTime) : 1440; // 24:00
                                        return (
                                          timeMinutes > startTimeMinutes && timeMinutes <= maxTimeMinutes
                                        );
                                      })
                                      .map((time) => (
                                        <SelectItem key={time} value={time} className="text-sm">
                                          {time}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteTimeSlot(day.key, index)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 border-none ml-2"
                                aria-label="Eliminar horario"
                              >
                                <Trash2 className="h-5 w-5" />
                              </Button>
                            </div>
                          ) : (
                            // Modo vista
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap text-slate-600">
                                  <div
                                    className={classNames(
                                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium'
                                    )}
                                  >
                                    {startInfo.icon}
                                    <span>{slot.start}</span>
                                  </div>
                                  <div className="w-6 h-0.5 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full hidden sm:block" />
                                  <span className="text-gray-400 text-sm sm:hidden">→</span>
                                  <div
                                    className={classNames(
                                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium'
                                    )}
                                  >
                                    {endInfo.icon}
                                    <span>{slot.end}</span>
                                  </div>
                                </div>

                                {!isInvalid && duration > 0 && (
                                  <Badge
                                    variant="outline"
                                    className="bg-slate-50 text-slate-600 border-slate-200 text-xs font-medium ml-2"
                                  >
                                    {durationHours}h {durationMinutes > 0 ? `${durationMinutes}m` : ''}
                                  </Badge>
                                )}
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteTimeSlot(day.key, index)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 shrink-0 ml-3 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Botón para agregar más horarios */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddTimeSlot(day.key)}
                    className="w-full text-sm border-dashed border-gray-400 text-gray-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 py-3 font-medium transition-all duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Agregar otro horario
                    </div>
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
